import {
  App as AntdApp,
  Button,
  Card,
  Flex,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import {
  CheckCircle2,
  KeyRound,
  Mail,
  Shield,
  Smartphone,
  ShieldCheck
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiPendingBadge } from 'src/features/organization/components'

import type { WorkspaceUser } from '../types'
import { deriveInviteState } from '../utils'

const { Text } = Typography

type Props = {
  record: WorkspaceUser
  /** False when the viewer holds `users.view` but not `users.manage`. */
  canManage?: boolean
  onResetPassword: () => void
  onRequireMfa: () => void
  onDisableMfa: () => void
  onResetMfa: () => void
}

const ROW_ICON_STYLE = {
  width: 30,
  height: 30,
  borderRadius: 7,
  background: 'var(--ant-color-fill-quaternary)',
  color: 'var(--ant-color-text-secondary)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto'
} as const

export const UserAuthenticationCard = ({
  record,
  canManage = true,
  onResetPassword,
  onRequireMfa,
  onDisableMfa,
  onResetMfa
}: Props) => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const hasSso = Boolean(record.user.sso_id)
  // Admin password reset only applies after the member accepts — before that the
  // path to a password is the invite/setup link (backend rejects the reset).
  const accepted = deriveInviteState(record) === 'accepted'
  // mfa_enabled = the member's own enrollment; mfa_required = whether this
  // workspace compels MFA (org policy or per-member override). Both are
  // admin-only and arrive null together for non-admin viewers.
  const mfaEnabled = record.mfa_enabled
  const mfaRequired = record.mfa_required

  const handleApiPending = (key: string) =>
    message.info(t(`users.detail.authentication.apiPending.${key}`))

  const passwordButton = !canManage ? null : (
    <Button
      size="small"
      disabled={hasSso || !accepted}
      onClick={onResetPassword}
    >
      {hasSso
        ? t('users.detail.authentication.sso')
        : t('users.actions.resetPassword')}
    </Button>
  )

  return (
    <Card
      size="small"
      title={
        <Text strong style={{ fontSize: 13 }}>
          {t('users.detail.authentication.title')}
        </Text>
      }
    >
      <Flex vertical gap={10}>
        {/* Password */}
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={10}>
            <span style={ROW_ICON_STYLE}>
              <KeyRound size={14} />
            </span>
            <Flex vertical style={{ lineHeight: 1.25 }}>
              <Text strong style={{ fontSize: 12.5 }}>
                {t('users.detail.authentication.password')}
              </Text>
              <Flex align="center" gap={4}>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {hasSso
                    ? t('users.detail.authentication.managedBySso')
                    : t('users.detail.authentication.passwordMeta')}
                </Text>
                {!hasSso ? <ApiPendingBadge iconOnly /> : null}
              </Flex>
            </Flex>
          </Flex>
          {passwordButton && !accepted && !hasSso ? (
            <Tooltip title={t('users.lifecycle.requiresAcceptance')}>
              <span>{passwordButton}</span>
            </Tooltip>
          ) : (
            passwordButton
          )}
        </Flex>

        {/* Two-factor — admin-only; the whole block is hidden when the API
            doesn't expose MFA to this viewer (non-admin → both flags arrive
            null). The two axes are independent and each gets its own row with
            ONLY the action that changes it:
              • Method = the member's own enrollment. Admin can Reset it for
                recovery but never switch the factor off — that stays the
                member's.
              • Requirement = whether THIS workspace compels MFA for the member
                (the Require / Stop-requiring toggle). */}
        {mfaRequired !== null && (
          <>
            {/* Method — the member's own second factor */}
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={10}>
                <span style={ROW_ICON_STYLE}>
                  <Smartphone size={14} />
                </span>
                <Flex vertical style={{ lineHeight: 1.25 }}>
                  <Text strong style={{ fontSize: 12.5 }}>
                    {t('users.detail.authentication.mfaMethod')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {mfaEnabled
                      ? t('users.detail.authentication.mfaMethodEnrolledMeta')
                      : t(
                          'users.detail.authentication.mfaMethodNotEnrolledMeta'
                        )}
                  </Text>
                </Flex>
              </Flex>
              <Flex align="center" gap={8}>
                {mfaEnabled ? (
                  <Tag color="success" style={{ marginInlineEnd: 0 }}>
                    {t('users.detail.authentication.mfaEnabled')}
                  </Tag>
                ) : (
                  <Tag style={{ marginInlineEnd: 0 }}>
                    {t('users.detail.authentication.mfaNotEnrolled')}
                  </Tag>
                )}
                {/* Reset is account recovery — only offered for a member who has
                    actually enrolled a factor and accepted their invite. */}
                {canManage && mfaEnabled && accepted ? (
                  <Button size="small" onClick={onResetMfa}>
                    {t('users.detail.authentication.reset')}
                  </Button>
                ) : null}
              </Flex>
            </Flex>

            {/* Requirement — whether this workspace compels MFA for the member */}
            <Flex align="center" justify="space-between">
              <Flex align="center" gap={10}>
                <span style={ROW_ICON_STYLE}>
                  <Shield size={14} />
                </span>
                <Flex vertical style={{ lineHeight: 1.25 }}>
                  <Text strong style={{ fontSize: 12.5 }}>
                    {t('users.detail.authentication.mfaRequire')}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {mfaRequired
                      ? t('users.detail.authentication.mfaRequireOnMeta')
                      : t('users.detail.authentication.mfaRequireOffMeta')}
                  </Text>
                </Flex>
              </Flex>
              <Flex align="center" gap={8}>
                {mfaRequired ? (
                  <>
                    <Tag color="processing" style={{ marginInlineEnd: 0 }}>
                      {t('users.detail.authentication.mfaRequiredTag')}
                    </Tag>
                    {canManage ? (
                      <Button size="small" onClick={onDisableMfa}>
                        {t('users.detail.authentication.disable')}
                      </Button>
                    ) : null}
                  </>
                ) : canManage ? (
                  <Button size="small" onClick={onRequireMfa}>
                    {t('users.detail.authentication.enforce')}
                  </Button>
                ) : null}
              </Flex>
            </Flex>
          </>
        )}

        {/* SSO */}
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={10}>
            <span style={ROW_ICON_STYLE}>
              <ShieldCheck size={14} />
            </span>
            <Flex vertical style={{ lineHeight: 1.25 }}>
              <Text strong style={{ fontSize: 12.5 }}>
                {t('users.detail.authentication.ssoProvider')}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {hasSso
                  ? t('users.detail.authentication.ssoLinkedMeta', {
                      id: record.user.sso_id
                    })
                  : t('users.detail.authentication.ssoNotLinked')}
              </Text>
            </Flex>
          </Flex>
          {hasSso ? (
            <Tag color="success" style={{ marginInlineEnd: 0 }}>
              {t('users.detail.authentication.linked')}
            </Tag>
          ) : (
            <Button size="small" onClick={() => handleApiPending('linkSso')}>
              {t('users.detail.authentication.link')}
            </Button>
          )}
        </Flex>

        {/* Email verified */}
        <Flex align="center" justify="space-between">
          <Flex align="center" gap={10}>
            <span style={ROW_ICON_STYLE}>
              <Mail size={14} />
            </span>
            <Flex vertical style={{ lineHeight: 1.25 }}>
              <Text strong style={{ fontSize: 12.5 }}>
                {t('users.detail.authentication.emailVerified')}
              </Text>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {record.user.email}
              </Text>
            </Flex>
          </Flex>
          <CheckCircle2 size={16} color="var(--ant-color-success)" />
        </Flex>
      </Flex>
    </Card>
  )
}
